import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import usePermissions from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../config/permissions';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const NIGERIAN_STATES = [
  'Abia','Adamawa','Akwa Ibom','Anambra','Bauchi','Bayelsa','Benue','Borno',
  'Cross River','Delta','Ebonyi','Edo','Ekiti','Enugu','FCT','Gombe','Imo',
  'Jigawa','Kaduna','Kano','Katsina','Kebbi','Kogi','Kwara','Lagos','Nasarawa',
  'Niger','Ogun','Ondo','Osun','Oyo','Plateau','Rivers','Sokoto','Taraba',
  'Yobe','Zamfara',
];

export default function AdminMembers() {
  const { canEdit, canAdd, canDelete } = usePermissions();
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Modal state
  const [viewMember, setViewMember] = useState(null);
  const [editMember, setEditMember] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editPhoto, setEditPhoto] = useState(null);
  const [editPhotoPreview, setEditPhotoPreview] = useState(null);
  const [deletePhoto, setDeletePhoto] = useState(false);
  const [editLogo, setEditLogo] = useState(null);
  const [editLogoPreview, setEditLogoPreview] = useState(null);
  const [deleteLogo, setDeleteLogo] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [deleteMember, setDeleteMember] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [stats, setStats] = useState({ fellow: 0, corporate: 0, associate: 0, graduate: 0, student: 0, technician: 0, probationer: 0 });

  // Create Member modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    email: '', first_name: '', last_name: '', password: '', password_confirm: '',
    phone_number: '', membership_category: '', membership_number: '', shortcode: '',
    surcon_number: '', firm_name: '', firm_address: '', practice_code: '',
  });
  const [createPhoto, setCreatePhoto] = useState(null);
  const [createPhotoPreview, setCreatePhotoPreview] = useState(null);
  const [createLogo, setCreateLogo] = useState(null);
  const [createLogoPreview, setCreateLogoPreview] = useState(null);

  const fetchStats = async () => {
    try {
      const res = await api.get('/accounts/admin/members/stats/');
      setStats(res.data);
    } catch { /* ignore */ }
  };

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page_size', '1000');
      if (statusFilter) params.append('membership_status', statusFilter);
      if (categoryFilter) params.append('membership_category', categoryFilter);
      const res = await api.get(`/accounts/admin/members/?${params}`);
      setMembers(res.data.results || res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMembers(); fetchStats(); }, []);
  useEffect(() => { fetchMembers(); }, [statusFilter, categoryFilter]);

  // Client-side search filtering — supports full-name search (e.g. "Yusuf Obaje Yunusa")
  const filteredMembers = members.filter((m) => {
    if (!search) return true;
    const q = search.toLowerCase().trim();
    // Build a combined string with all name permutations so any order matches
    const surname = (m.surname || '').toLowerCase();
    const otherNames = (m.other_names || '').toLowerCase();
    const combined = `${surname} ${otherNames} ${otherNames} ${surname} ${m.full_name || ''} ${m.email || ''} ${m.membership_number || ''} ${m.phone_number || ''}`.toLowerCase();
    // Match if the whole query appears as-is, OR every word appears somewhere
    if (combined.includes(q)) return true;
    const words = q.split(/\s+/).filter(Boolean);
    return words.length > 1 && words.every(w => combined.includes(w));
  });

  // Reset to page 1 when filtered list or page size changes
  useEffect(() => { setCurrentPage(1); }, [search, members, pageSize]);

  const totalPages = pageSize === 'all' ? 1 : Math.ceil(filteredMembers.length / pageSize);
  const paginatedMembers = pageSize === 'all'
    ? filteredMembers
    : filteredMembers.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const approveMember = async (id) => {
    try {
      await api.post(`/accounts/admin/members/${id}/approve/`);
      toast.success('Member approved!');
      fetchMembers();
    } catch {
      toast.error('Failed to approve.');
    }
  };

  const toggleMemberStatus = async (id, newStatus) => {
    try {
      await api.post(`/accounts/admin/members/${id}/set-status/`, { status: newStatus });
      toast.success(`Member marked as ${newStatus}.`);
      fetchMembers();
      fetchStats();
    } catch {
      toast.error('Failed to update status.');
    }
  };

  const [syncingDues, setSyncingDues] = useState(false);
  const syncDuesStatus = async () => {
    setSyncingDues(true);
    try {
      const res = await api.post('/accounts/admin/members/sync-dues-status/');
      toast.success(res.data.detail);
      fetchMembers();
      fetchStats();
    } catch {
      toast.error('Failed to sync dues status.');
    } finally {
      setSyncingDues(false);
    }
  };

  const openViewModal = async (id) => {
    try {
      const res = await api.get(`/accounts/admin/members/${id}/`);
      setViewMember(res.data);
    } catch {
      toast.error('Failed to load member details.');
    }
  };

  const openEditModal = async (id) => {
    try {
      const res = await api.get(`/accounts/admin/members/${id}/`);
      setEditMember(res.data);
      setEditPhoto(null);
      setEditPhotoPreview(res.data.photo || null);
      setDeletePhoto(false);
      setEditLogo(null);
      setEditLogoPreview(res.data.company_logo || null);
      setDeleteLogo(false);
      setNewPassword('');
      setEditForm({
        first_name: res.data.user?.first_name || '',
        last_name: res.data.user?.last_name || '',
        email: res.data.user?.email || '',
        phone_number: res.data.phone_number || '',
        membership_category: res.data.membership_category || '',
        membership_number: res.data.membership_number || '',
        surcon_number: res.data.surcon_number || '',
        firm_name: res.data.firm_name || '',
        firm_address: res.data.firm_address || '',
        practice_code: res.data.practice_code || '',
      });
    } catch {
      toast.error('Failed to load member details.');
    }
  };

  const handleEditPhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Photo must be less than 2MB');
        return;
      }
      setEditPhoto(file);
      setEditPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleEditSave = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(editForm).forEach(([key, val]) => {
        if (val !== '' && val !== null && val !== undefined) {
          fd.append(key, val);
        }
      });
      // Always send user-level fields even if empty
      fd.set('first_name', editForm.first_name || '');
      fd.set('last_name', editForm.last_name || '');
      fd.set('email', editForm.email || '');
      if (editPhoto) fd.append('photo', editPhoto);
      if (deletePhoto) fd.append('photo', '');
      if (editLogo) fd.append('company_logo', editLogo);
      if (deleteLogo) fd.append('company_logo', '');
      await api.patch(`/accounts/admin/members/${editMember.id}/`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Member updated successfully!');
      setEditMember(null);
      setEditPhoto(null);
      setEditPhotoPreview(null);
      setDeletePhoto(false);
      setEditLogo(null);
      setEditLogoPreview(null);
      setDeleteLogo(false);
      setNewPassword('');
      fetchMembers();
    } catch (err) {
      const data = err.response?.data;
      if (data?.email) {
        toast.error(Array.isArray(data.email) ? data.email[0] : data.email);
      } else {
        toast.error('Failed to update member.');
      }
    } finally {
      setSaving(false);
    }
  };

  const downloadMemberPDF = async (member) => {
    const doc = new jsPDF();
    const green = [11, 93, 30];

    // Header bar
    doc.setFillColor(...green);
    doc.rect(0, 0, 210, 36, 'F');

    // Load logo into header
    try {
      const logoImg = new Image();
      await new Promise((resolve, reject) => {
        logoImg.onload = resolve;
        logoImg.onerror = reject;
        logoImg.src = '/APPSN.jpg';
      });
      const logoCanvas = document.createElement('canvas');
      logoCanvas.width = logoImg.width;
      logoCanvas.height = logoImg.height;
      logoCanvas.getContext('2d').drawImage(logoImg, 0, 0);
      const logoData = logoCanvas.toDataURL('image/jpeg', 0.9);
      doc.addImage(logoData, 'JPEG', 14, 4, 28, 28);
    } catch {
      // Logo failed to load — continue without it
    }

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('APPSN - FCT Branch', 120, 16, { align: 'center' });
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text('Member Profile', 120, 25, { align: 'center' });

    // Load member photo if available
    let photoLoaded = false;
    if (member.photo) {
      try {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = member.photo;
        });
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const imgData = canvas.toDataURL('image/jpeg', 0.85);
        // Preserve aspect ratio within a 25mm box
        const maxW = 25;
        const maxH = 30;
        const ratio = Math.min(maxW / img.width, maxH / img.height);
        const imgW = img.width * ratio;
        const imgH = img.height * ratio;
        doc.addImage(imgData, 'JPEG', 14, 42, imgW, imgH);
        photoLoaded = true;
      } catch {
        // Photo failed to load — skip it
      }
    }

    // Member name & status (shift right if photo is present)
    const textX = photoLoaded ? 44 : 14;
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(member.full_name || '—', textX, 52);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(member.user?.email || '', textX, 59);
    doc.text(`Status: ${(member.membership_status || '—').toUpperCase()}   |   Category: ${(member.membership_category || '—').toUpperCase()}`, textX, 65);

    // Company logo (top-right, aligned with passport photo)
    if (member.company_logo) {
      try {
        const cImg = new Image();
        cImg.crossOrigin = 'anonymous';
        await new Promise((resolve, reject) => {
          cImg.onload = resolve;
          cImg.onerror = reject;
          cImg.src = member.company_logo;
        });
        const cCanvas = document.createElement('canvas');
        cCanvas.width = cImg.width;
        cCanvas.height = cImg.height;
        cCanvas.getContext('2d').drawImage(cImg, 0, 0);
        const cData = cCanvas.toDataURL('image/jpeg', 0.85);
        const cMaxW = 25;
        const cMaxH = 28;
        const cRatio = Math.min(cMaxW / cImg.width, cMaxH / cImg.height);
        const cW = cImg.width * cRatio;
        const cH = cImg.height * cRatio;
        doc.addImage(cData, 'JPEG', 196 - cW, 42, cW, cH);
      } catch {
        // Company logo failed to load — skip it
      }
    }

    // Profile table
    const rows = [
      ['Surname', member.surname || member.user?.last_name],
      ['Other Names', member.other_names || member.user?.first_name],
      ['Phone Number', member.phone_number],
      ['Member Status', member.membership_category],
      ['NIS Membership ID', member.membership_number],
      ['SURCON No.', member.surcon_number],
      ['Company Name', member.firm_name],
      ['Company Address', member.firm_address],
      ['Practice Code', member.practice_code],
      ['Email', member.user?.email],
    ].map(([label, val]) => [label, val || '—']);

    autoTable(doc, {
      startY: 72,
      head: [['Field', 'Details']],
      body: rows,
      theme: 'striped',
      headStyles: { fillColor: green, textColor: [255, 255, 255], fontStyle: 'bold' },
      styles: { fontSize: 10, cellPadding: 4 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55 } },
    });

    // Footer
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(`Generated on ${new Date().toLocaleDateString()}`, 14, pageHeight - 10);
    doc.text('APPSN - FCT Branch', 196, pageHeight - 10, { align: 'right' });

    const safeName = (member.full_name || 'member').replace(/\s+/g, '_');
    doc.save(`${safeName}_profile.pdf`);
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await api.delete(`/accounts/admin/members/${deleteMember.id}/`);
      toast.success('Member deleted successfully.');
      setDeleteMember(null);
      setSelectedIds((prev) => { const next = new Set(prev); next.delete(deleteMember.id); return next; });
      fetchMembers();
      fetchStats();
    } catch {
      toast.error('Failed to delete member.');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkDelete = async () => {
    setSaving(true);
    let deleted = 0;
    let failed = 0;
    for (const id of selectedIds) {
      try {
        await api.delete(`/accounts/admin/members/${id}/`);
        deleted++;
      } catch {
        failed++;
      }
    }
    setSaving(false);
    setBulkDeleteConfirm(false);
    setSelectedIds(new Set());
    if (deleted) toast.success(`${deleted} member${deleted > 1 ? 's' : ''} deleted.`);
    if (failed) toast.error(`${failed} member${failed > 1 ? 's' : ''} failed to delete.`);
    fetchMembers();
    fetchStats();
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const pageIds = paginatedMembers.map((m) => m.id);
    const allSelected = pageIds.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      pageIds.forEach((id) => allSelected ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const resetCreateForm = () => {
    setCreateForm({
      email: '', first_name: '', last_name: '', password: '', password_confirm: '',
      phone_number: '', membership_category: '', membership_number: '', shortcode: '',
      surcon_number: '', firm_name: '', firm_address: '', practice_code: '',
    });
    setCreatePhoto(null);
    setCreatePhotoPreview(null);
    setCreateLogo(null);
    setCreateLogoPreview(null);
    setShowCreateModal(false);
  };

  const handleCreateMember = async () => {
    if (!createForm.email || !createForm.first_name || !createForm.last_name) {
      toast.error('Email, surname and other names are required.');
      return;
    }
    if (createForm.password.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    if (createForm.password !== createForm.password_confirm) {
      toast.error('Passwords do not match.');
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(createForm).forEach(([key, val]) => {
        if (key === 'password_confirm') return;
        if (val !== '' && val !== null && val !== undefined) {
          fd.append(key, val);
        }
      });
      if (createPhoto) fd.append('photo', createPhoto);
      if (createLogo) fd.append('company_logo', createLogo);
      await api.post('/accounts/admin/members/create/', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Member created successfully!');
      resetCreateForm();
      fetchMembers();
      fetchStats();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to create member.';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Members Management</h1>
        <div className="flex items-center gap-2">
          {canEdit(PERMISSIONS.EDIT_MEMBERS) && (
            <button onClick={syncDuesStatus} disabled={syncingDues}
              className="bg-orange-500 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-orange-600 transition inline-flex items-center gap-2 disabled:opacity-60"
              title="Mark all active members with unpaid dues as inactive">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {syncingDues ? 'Syncing...' : 'Sync Dues Status'}
            </button>
          )}
          {canAdd(PERMISSIONS.ADD_MEMBERS) && (
            <button onClick={() => setShowCreateModal(true)}
              className="bg-[#0B5D1E] text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-[#062E10] transition inline-flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add Member
            </button>
          )}
        </div>
      </div>

      {/* Category Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Fellow', key: 'fellow', color: 'bg-purple-600', icon: 'M12 14l9-5-9-5-9 5 9 5z' },
          { label: 'Corporate', key: 'corporate', color: 'bg-blue-600', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
          { label: 'Associate', key: 'associate', color: 'bg-green-600', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z' },
          { label: 'Probationer', key: 'probationer', color: 'bg-orange-500', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
        ].map(({ label, key, color, icon }) => (
            <div key={key} className="bg-white rounded-xl shadow-sm border p-4 flex items-center gap-4">
              <div className={`${color} w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0`}>
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats[key] ?? 0}</p>
                <p className="text-xs text-gray-500 font-medium">{label}</p>
              </div>
            </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border p-4 mb-6">
        <form onSubmit={(e) => { e.preventDefault(); fetchMembers(); }} className="flex flex-col md:flex-row gap-3">
          <input type="text" placeholder="Search by name, email, membership number..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none" />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none">
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none">
            <option value="">All Categories</option>
            <option value="fellow">Fellow</option>
            <option value="corporate">Corporate</option>
            <option value="associate">Associate</option>
            <option value="graduate">Graduate</option>
            <option value="student">Student</option>
            <option value="technician">Technician</option>
            <option value="probationer">Probationer</option>
          </select>
          <button type="submit"
            className="bg-[#0B5D1E] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#062E10] transition">
            Search
          </button>
        </form>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 flex items-center justify-between">
          <span className="text-sm text-red-700 font-medium">
            {selectedIds.size} member{selectedIds.size > 1 ? 's' : ''} selected
          </span>
          <div className="flex items-center gap-2">
            <button onClick={() => setSelectedIds(new Set())}
              className="text-sm px-3 py-1.5 bg-white border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition">
              Clear Selection
            </button>
            {canDelete(PERMISSIONS.DELETE_MEMBERS) && (
              <button onClick={() => setBulkDeleteConfirm(true)}
                className="text-sm px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition inline-flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete Selected
              </button>
            )}
          </div>
        </div>
      )}

      {/* Members Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-4 py-3">
                <input type="checkbox"
                  checked={paginatedMembers.length > 0 && paginatedMembers.every((m) => selectedIds.has(m.id))}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 rounded border-gray-300 text-[#0B5D1E] focus:ring-[#0B5D1E] cursor-pointer" />
              </th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">S/N</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Surname</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Other Names</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">ID</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Category</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Dues</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? (
              <tr><td colSpan="9" className="text-center py-8 text-gray-500">Loading...</td></tr>
            ) : filteredMembers.length === 0 ? (
              <tr><td colSpan="9" className="text-center py-8 text-gray-500">No members found.</td></tr>
            ) : paginatedMembers.map((m, idx) => (
              <tr key={m.id} className={`hover:bg-gray-50 ${selectedIds.has(m.id) ? 'bg-blue-50' : ''}`}>
                <td className="px-4 py-4">
                  <input type="checkbox"
                    checked={selectedIds.has(m.id)}
                    onChange={() => toggleSelect(m.id)}
                    className="w-4 h-4 rounded border-gray-300 text-[#0B5D1E] focus:ring-[#0B5D1E] cursor-pointer" />
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">{(pageSize === 'all' ? 0 : (currentPage - 1) * pageSize) + idx + 1}</td>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-3">
                    {m.photo ? (
                      <img src={m.photo} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#0B5D1E] text-white flex items-center justify-center font-bold text-sm">
                        {(m.surname || '').charAt(0)}
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-sm">{m.surname}</p>
                      <p className="text-xs text-gray-500">{m.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm">{m.other_names}</td>
                <td className="px-6 py-4 text-sm font-mono">{m.membership_number || '—'}</td>
                <td className="px-6 py-4 text-sm capitalize">{m.membership_category}</td>
                <td className="px-6 py-4">
                  <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
                    m.membership_status === 'active' ? 'bg-green-100 text-green-700' :
                    m.membership_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                    m.membership_status === 'suspended' ? 'bg-red-100 text-red-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>{m.membership_status}</span>
                  {m.membership_status === 'active' && m.dues_status !== 'paid' && (
                    <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 font-semibold" title="Owes annual dues">Dues Owing</span>
                  )}
                </td>
                <td className="px-6 py-4">
                  <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
                    m.dues_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>{m.dues_status}</span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <button onClick={() => openViewModal(m.id)}
                      className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 transition inline-flex items-center gap-1"
                      title="View Details">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      View
                    </button>
                    {canEdit(PERMISSIONS.EDIT_MEMBERS) && (
                      <button onClick={() => openEditModal(m.id)}
                        className="text-xs bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600 transition inline-flex items-center gap-1"
                        title="Edit Member">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit
                      </button>
                    )}
                    {canDelete(PERMISSIONS.DELETE_MEMBERS) && (
                      <button onClick={() => setDeleteMember(m)}
                        className="text-xs bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition inline-flex items-center gap-1"
                        title="Delete Member">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Delete
                      </button>
                    )}
                    <button onClick={async () => {
                        try {
                          const res = await api.get(`/accounts/admin/members/${m.id}/`);
                          await downloadMemberPDF(res.data);
                        } catch { toast.error('Failed to download PDF.'); }
                      }}
                      className="text-xs bg-purple-600 text-white px-3 py-1 rounded hover:bg-purple-700 transition inline-flex items-center gap-1"
                      title="Download PDF">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      PDF
                    </button>
                    {canEdit(PERMISSIONS.EDIT_MEMBERS) && m.membership_status !== 'active' && (
                      <button onClick={() => toggleMemberStatus(m.id, 'active')}
                        className="text-xs bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 transition inline-flex items-center gap-1"
                        title="Set Active">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Activate
                      </button>
                    )}
                    {canEdit(PERMISSIONS.EDIT_MEMBERS) && m.membership_status === 'active' && (
                      <button onClick={() => toggleMemberStatus(m.id, 'inactive')}
                        className="text-xs bg-orange-500 text-white px-3 py-1 rounded hover:bg-orange-600 transition inline-flex items-center gap-1"
                        title="Set Inactive">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                        </svg>
                        Deactivate
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination Controls */}
        {filteredMembers.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between px-6 py-4 border-t bg-gray-50 gap-3">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Show</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                className="px-2 py-1 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value="all">All</option>
              </select>
              <span>of {filteredMembers.length} records</span>
            </div>

            {pageSize !== 'all' && totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-2 py-1 text-sm border rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  &laquo;
                </button>
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-2 py-1 text-sm border rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  &lsaquo;
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                  .reduce((acc, p, idx, arr) => {
                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, idx) =>
                    p === '...' ? (
                      <span key={`dot-${idx}`} className="px-2 py-1 text-sm text-gray-400">...</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setCurrentPage(p)}
                        className={`px-3 py-1 text-sm border rounded ${
                          currentPage === p
                            ? 'bg-[#0B5D1E] text-white border-[#0B5D1E]'
                            : 'hover:bg-gray-100'
                        }`}
                      >
                        {p}
                      </button>
                    )
                  )}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-2 py-1 text-sm border rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  &rsaquo;
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-2 py-1 text-sm border rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  &raquo;
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ——— View Member Modal ——— */}
      {viewMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">Member Details</h2>
              <button onClick={() => setViewMember(null)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>
            <div className="p-6">
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-4">
                  {viewMember.photo ? (
                    <img src={viewMember.photo} alt="" className="w-20 h-20 rounded-full object-cover" />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-[#0B5D1E] text-white flex items-center justify-center font-bold text-2xl">
                      {viewMember.full_name?.charAt(0)}
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg font-semibold">{viewMember.full_name}</h3>
                    <p className="text-sm text-gray-500">{viewMember.user?.email}</p>
                    <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
                      viewMember.membership_status === 'active' ? 'bg-green-100 text-green-700' :
                      viewMember.membership_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>{viewMember.membership_status}</span>
                  </div>
                </div>
                {viewMember.company_logo && (
                  <img src={viewMember.company_logo} alt="Company Logo" className="w-24 h-24 rounded-lg object-contain border border-gray-200 bg-white p-1" />
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {[
                  ['Surname', viewMember.surname || viewMember.user?.last_name],
                  ['Other Names', viewMember.other_names || viewMember.user?.first_name],
                  ['Phone Number', viewMember.phone_number],
                  ['Member Status', viewMember.membership_category],
                  ['NIS Membership ID', viewMember.membership_number],
                  ['SURCON No.', viewMember.surcon_number],
                  ['Company Name', viewMember.firm_name],
                  ['Company Address', viewMember.firm_address],
                  ['Practice Code', viewMember.practice_code],
                  ['Email', viewMember.user?.email],
                ].map(([label, value]) => (
                  <div key={label}>
                    <p className="text-gray-500 text-xs">{label}</p>
                    <p className="font-medium capitalize">{value || '—'}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button onClick={() => downloadMemberPDF(viewMember)}
                className="px-4 py-2 bg-[#0B5D1E] text-white rounded-lg hover:bg-[#062E10] transition inline-flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download PDF
              </button>
              <button onClick={() => setViewMember(null)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ——— Edit Member Modal ——— */}
      {editMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">Edit Member — {editMember.full_name}</h2>
              <button onClick={() => setEditMember(null)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>
            <div className="p-6 space-y-6">

              {/* Photo Upload */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-2">Passport Photograph</label>
                <div className="flex items-center gap-4">
                  {editPhotoPreview ? (
                    <div className="relative">
                      <img src={editPhotoPreview} alt="Preview" className="w-20 h-20 rounded-full object-cover border-2 border-gray-200" />
                      <button type="button" onClick={() => { setEditPhoto(null); setEditPhotoPreview(null); setDeletePhoto(true); }}
                        className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition shadow-sm"
                        title="Remove photo">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                  )}
                  <div>
                    <label className="cursor-pointer inline-block px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-sm font-medium">
                      Choose Photo
                      <input type="file" accept="image/*" onChange={handleEditPhotoChange} className="hidden" />
                    </label>
                    <p className="text-xs text-gray-400 mt-1">Max 2MB — JPG, PNG</p>
                  </div>
                </div>
              </div>

              {/* Name & Email */}
              <div>
                <h3 className="text-sm font-bold text-gray-700 mb-3 border-b pb-1">Personal Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Surname</label>
                    <input type="text" value={editForm.last_name}
                      onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Other Names</label>
                    <input type="text" value={editForm.first_name}
                      onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Phone Number</label>
                    <input type="tel" value={editForm.phone_number}
                      onChange={(e) => setEditForm({ ...editForm, phone_number: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Member Status</label>
                    <select value={editForm.membership_category}
                      onChange={(e) => setEditForm({ ...editForm, membership_category: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm bg-white">
                      <option value="">— Select —</option>
                      <option value="fellow">Fellow (FNIS)</option>
                      <option value="corporate">Corporate Member (MNIS)</option>
                      <option value="associate">Associate Member</option>
                      <option value="probationer">Probationer</option>
                      <option value="technician">Technician Member</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">NIS Membership ID</label>
                    <input type="text" value={editForm.membership_number}
                      onChange={(e) => setEditForm({ ...editForm, membership_number: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">SURCON No.</label>
                    <input type="text" value={editForm.surcon_number}
                      onChange={(e) => setEditForm({ ...editForm, surcon_number: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                </div>
              </div>

              {/* Company Information */}
              <div>
                <h3 className="text-sm font-bold text-gray-700 mb-3 border-b pb-1">Company Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Company Name</label>
                    <input type="text" value={editForm.firm_name}
                      onChange={(e) => setEditForm({ ...editForm, firm_name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>

                  {/* Company Logo */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-2">Company Logo</label>
                    <div className="flex items-center gap-4">
                      {editLogoPreview ? (
                        <div className="relative">
                          <img src={editLogoPreview} alt="Logo" className="w-16 h-16 rounded-lg object-contain border-2 border-gray-200 bg-white p-1" />
                          <button type="button" onClick={() => { setEditLogo(null); setEditLogoPreview(null); setDeleteLogo(true); }}
                            className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition shadow-sm"
                            title="Remove logo">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400">
                          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </div>
                      )}
                      <div>
                        <label className="cursor-pointer inline-block px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-sm font-medium">
                          Choose Logo
                          <input type="file" accept="image/*" onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              if (file.size > 2 * 1024 * 1024) { toast.error('Logo must be less than 2MB'); return; }
                              setEditLogo(file);
                              setEditLogoPreview(URL.createObjectURL(file));
                            }
                          }} className="hidden" />
                        </label>
                        <p className="text-xs text-gray-400 mt-1">Max 2MB — JPG, PNG</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Company Address</label>
                    <textarea value={editForm.firm_address} rows={2}
                      onChange={(e) => setEditForm({ ...editForm, firm_address: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Practice Code</label>
                    <input type="text" value={editForm.practice_code}
                      onChange={(e) => setEditForm({ ...editForm, practice_code: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                </div>
              </div>

              {/* Account */}
              <div>
                <h3 className="text-sm font-bold text-gray-700 mb-3 border-b pb-1">Account</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Email Address</label>
                    <input type="email" value={editForm.email}
                      onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                    <p className="text-xs text-amber-600 mt-1">Changing the email will update the member's login email.</p>
                  </div>
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <label className="block text-xs font-semibold text-gray-500 mb-1">Reset Password</label>
                      <input type="password" value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Min. 8 characters"
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                    </div>
                    <button type="button" disabled={saving || newPassword.length < 8}
                      onClick={async () => {
                        setSaving(true);
                        try {
                          await api.post(`/accounts/admin/members/${editMember.id}/reset-password/`, { new_password: newPassword });
                          toast.success('Password reset successfully!');
                          setNewPassword('');
                        } catch {
                          toast.error('Failed to reset password.');
                        } finally {
                          setSaving(false);
                        }
                      }}
                      className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition text-sm font-semibold disabled:opacity-50 whitespace-nowrap">
                      Reset
                    </button>
                  </div>
                  <p className="text-xs text-gray-400">This will immediately change the member's login password.</p>
                </div>
              </div>

            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button onClick={() => setEditMember(null)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition">
                Cancel
              </button>
              <button onClick={handleEditSave} disabled={saving}
                className="px-4 py-2 bg-[#0B5D1E] text-white rounded-lg hover:bg-[#062E10] transition disabled:opacity-50">
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ——— Bulk Delete Confirmation Modal ——— */}
      {bulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h2 className="text-lg font-bold text-gray-900">Delete {selectedIds.size} Member{selectedIds.size > 1 ? 's' : ''}</h2>
              </div>
              <p className="text-sm text-gray-600">
                Are you sure you want to delete <strong>{selectedIds.size} selected member{selectedIds.size > 1 ? 's' : ''}</strong>? This action cannot be undone and will permanently remove their accounts and all associated data.
              </p>
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button onClick={() => setBulkDeleteConfirm(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition">
                Cancel
              </button>
              <button onClick={handleBulkDelete} disabled={saving}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50">
                {saving ? 'Deleting...' : `Delete ${selectedIds.size} Member${selectedIds.size > 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ——— Delete Confirmation Modal ——— */}
      {deleteMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h2 className="text-lg font-bold text-gray-900">Delete Member</h2>
              </div>
              <p className="text-sm text-gray-600">
                Are you sure you want to delete <strong>{deleteMember.full_name}</strong>? This action cannot be undone and will permanently remove their account and all associated data.
              </p>
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button onClick={() => setDeleteMember(null)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition">
                Cancel
              </button>
              <button onClick={handleDelete} disabled={saving}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50">
                {saving ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ——— Create Member Modal ——— */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">Add New Member</h2>
              <button onClick={resetCreateForm} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>
            <div className="p-6 space-y-6">

              {/* ─── Personal Information ─── */}
              <div>
                <h3 className="text-sm font-bold text-gray-700 mb-3 border-b pb-1">Personal Information</h3>

                {/* Passport Photo */}
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-500 mb-2">Passport Photograph</label>
                  <div className="flex items-center gap-4">
                    {createPhotoPreview ? (
                      <div className="relative">
                        <img src={createPhotoPreview} alt="Preview" className="w-20 h-20 rounded-full object-cover border-2 border-gray-200" />
                        <button type="button" onClick={() => { setCreatePhoto(null); setCreatePhotoPreview(null); }}
                          className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition shadow-sm"
                          title="Remove photo">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                    )}
                    <div>
                      <label className="cursor-pointer inline-block px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-sm font-medium">
                        Choose Photo
                        <input type="file" accept="image/*" onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            if (file.size > 2 * 1024 * 1024) { toast.error('Photo must be less than 2MB'); return; }
                            setCreatePhoto(file);
                            setCreatePhotoPreview(URL.createObjectURL(file));
                          }
                        }} className="hidden" />
                      </label>
                      <p className="text-xs text-gray-400 mt-1">Max 2MB — JPG, PNG</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Surname <span className="text-red-500">*</span></label>
                    <input type="text" required value={createForm.last_name}
                      onChange={(e) => setCreateForm({ ...createForm, last_name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Other Names <span className="text-red-500">*</span></label>
                    <input type="text" required value={createForm.first_name}
                      onChange={(e) => setCreateForm({ ...createForm, first_name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Phone Number</label>
                    <input type="tel" value={createForm.phone_number}
                      onChange={(e) => setCreateForm({ ...createForm, phone_number: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Member Status</label>
                    <select value={createForm.membership_category}
                      onChange={(e) => setCreateForm({ ...createForm, membership_category: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm bg-white">
                      <option value="">— Select —</option>
                      <option value="fellow">Fellow (FNIS)</option>
                      <option value="corporate">Corporate Member (MNIS)</option>
                      <option value="associate">Associate Member</option>
                      <option value="probationer">Probationer</option>
                      <option value="technician">Technician Member</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">NIS Membership ID</label>
                    <input type="text" value={createForm.membership_number}
                      onChange={(e) => setCreateForm({ ...createForm, membership_number: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">NIS Shortcode</label>
                    <input type="text" value={createForm.shortcode}
                      onChange={(e) => setCreateForm({ ...createForm, shortcode: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">SURCON No.</label>
                    <input type="text" value={createForm.surcon_number}
                      onChange={(e) => setCreateForm({ ...createForm, surcon_number: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                </div>
              </div>

              {/* ─── Company Information ─── */}
              <div>
                <h3 className="text-sm font-bold text-gray-700 mb-3 border-b pb-1">Company Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Company Name</label>
                    <input type="text" value={createForm.firm_name}
                      onChange={(e) => setCreateForm({ ...createForm, firm_name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>

                  {/* Company Logo */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-2">Company Logo</label>
                    <div className="flex items-center gap-4">
                      {createLogoPreview ? (
                        <div className="relative">
                          <img src={createLogoPreview} alt="Logo" className="w-16 h-16 rounded-lg object-contain border-2 border-gray-200 bg-white p-1" />
                          <button type="button" onClick={() => { setCreateLogo(null); setCreateLogoPreview(null); }}
                            className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition shadow-sm"
                            title="Remove logo">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400">
                          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </div>
                      )}
                      <div>
                        <label className="cursor-pointer inline-block px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-sm font-medium">
                          Choose Logo
                          <input type="file" accept="image/*" onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              if (file.size > 2 * 1024 * 1024) { toast.error('Logo must be less than 2MB'); return; }
                              setCreateLogo(file);
                              setCreateLogoPreview(URL.createObjectURL(file));
                            }
                          }} className="hidden" />
                        </label>
                        <p className="text-xs text-gray-400 mt-1">Max 2MB — JPG, PNG</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Company Address</label>
                    <textarea value={createForm.firm_address} rows={2}
                      onChange={(e) => setCreateForm({ ...createForm, firm_address: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Practice Code</label>
                    <input type="text" value={createForm.practice_code}
                      onChange={(e) => setCreateForm({ ...createForm, practice_code: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                </div>
              </div>

              {/* ─── Account ─── */}
              <div>
                <h3 className="text-sm font-bold text-gray-700 mb-3 border-b pb-1">Account</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Email Address <span className="text-red-500">*</span></label>
                    <input type="email" required value={createForm.email}
                      onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Password <span className="text-red-500">*</span></label>
                    <input type="password" required value={createForm.password}
                      onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                      placeholder="Min. 8 characters"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Confirm Password <span className="text-red-500">*</span></label>
                    <input type="password" required value={createForm.password_confirm}
                      onChange={(e) => setCreateForm({ ...createForm, password_confirm: e.target.value })}
                      placeholder="Re-enter password"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                </div>
              </div>

            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button onClick={resetCreateForm}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition">
                Cancel
              </button>
              <button onClick={handleCreateMember} disabled={saving}
                className="px-4 py-2 bg-[#0B5D1E] text-white rounded-lg hover:bg-[#062E10] transition disabled:opacity-50">
                {saving ? 'Creating...' : 'Add Member'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
